import { useEffect, useMemo, useRef, useState } from "react";
import { Patient } from "../../../types/patient";
import AuthService from "../../services/AuthService";
import {
  createPatient,
  listPatients,
  updatePatient,
  deletePatient,
} from "../../services/patientApi";
import { getReportsByCase, updateReport } from "../../services/reportAPI";
import { Box, Stack } from "@mui/system";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
  Snackbar,
  useMediaQuery,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Avatar,
} from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PatientFormDialog from "./PatientFormDialog";
import dayjs from "dayjs";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ChatIcon from "@mui/icons-material/Chat";
import { Collapse } from "@mui/material";
import { listPatientCases } from "../../services/patientApi";
import { listUserCases, updateCase } from "../../services/caseApi";
import React from "react";
import { useNavigate } from "react-router-dom";
import TourGuide from "../../../features/components/TourGuide";
import SchoolIcon from "@mui/icons-material/School";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import CakeIcon from "@mui/icons-material/Cake";
import WcIcon from "@mui/icons-material/Wc";
import FolderIcon from "@mui/icons-material/Folder";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useSubscription } from "../../hooks/useSubscription";

const sexes = ["female", "male", "other", "unknown"];
///Should also have orphan cases for doctors to play with
export default function PatientSpace() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const {
    hasActiveSubscription,
    isAdvanced,
    isProfessional,
    hasSubscription,
    isAdmin,
    loading: loading2,
  } = useSubscription();

  // hasSubscription() - specific tier check
  // hasSubscription('simple')       // true if Simple, Advanced, OR Professional
  // hasSubscription('advanced')     // true if Advanced OR Professional
  // hasSubscription('professional') // true ONLY if Professional

  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Add state for tour guide
  const [runTour, setRunTour] = useState(false);

  // edit state
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Patient | null>(null);
  const [editForm, setEditForm] = useState({
    givenName: "",
    familyName: "",
    dateOfBirth: "",
    sex: "",
    notes: "",
  });
  const [editErrors, setEditErrors] = useState({
    givenName: "",
    familyName: "",
    general: "",
  });

  // Orphaned cases state
  const [orphanedCases, setOrphanedCases] = useState<any[]>([]);
  const [orphanedCasesLoading, setOrphanedCasesLoading] = useState(false);
  const [orphanedCasesOffset, setOrphanedCasesOffset] = useState(0);
  const [hasMoreOrphanedCases, setHasMoreOrphanedCases] = useState(true);
  const [orphanedCasesSearch, setOrphanedCasesSearch] = useState("");
  const orphanedCasesLimit = 10;

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState<Patient | null>(null);
  
  // Copy notification state
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [copyMessage, setCopyMessage] = useState("");

  // Bottom sheet state for mobile actions
  const [actionSheetOpen, setActionSheetOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [caseActionSheetOpen, setCaseActionSheetOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState<any>(null);

  // Responsive breakpoint
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const authService = new AuthService();
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const [hasMore, setHasMore] = useState(true);
  function mergeUniqueById<T extends { id: string }>(
    existing: T[],
    incoming: T[],
  ) {
    const map = new Map(existing.map((x) => [x.id, x]));
    for (const x of incoming) map.set(x.id, x); // incoming overwrites existing
    return Array.from(map.values());
  }

  // // fetch patients (with search, load, reset)
  async function fetchPatients(search?: string, reset = false) {
    setLoading(true);
    try {
      const token = await authService.getAccessToken();
      if (!token) throw new Error("No access token available");

      // Important: If doing a search or reset, we need to start from offset 0
      if (reset || search !== undefined) {
        setOffset(0);
        const data = await listPatients(token, search, limit, 0);
        setPatients(data);
        setHasMore(data.length === limit);
        setOffset(data.length); // Set offset to length of returned data
      } else {
        // Normal pagination - load more with current offset
        const data = await listPatients(token, q, limit, offset);
        setPatients((prev) => mergeUniqueById(prev, data));
        setHasMore(data.length === limit);
        setOffset(offset + data.length);
      }
    } finally {
      setLoading(false);
    }
  }
  // useEffect(() => {
  //   fetchPatients();
  // }, []);
  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return; // prevents the second StrictMode call
    didInit.current = true;
    fetchPatients(undefined, /*reset=*/ true);
    fetchOrphanedCases(true);
  }, []);
  const rows = useMemo(() => patients, [patients]);

  const [caseOffset, setCaseOffset] = useState<Record<string, number>>({});
  const [caseHasMore, setCaseHasMore] = useState<Record<string, boolean>>({});
  const [caseSearchQueries, setCaseSearchQueries] = useState<
    Record<string, string>
  >({});
  const caseLimit = 10;

  async function loadCases(patientId: string, reset = false, search?: string) {
    setCasesLoading((prev) => ({ ...prev, [patientId]: true }));
    try {
      const token = await authService.getAccessToken();
      const offset = reset ? 0 : (caseOffset[patientId] ?? 0);

      // Use existing search if not provided
      const searchQuery =
        search !== undefined ? search : caseSearchQueries[patientId] || "";

      // Update search query state if it changed
      if (search !== undefined && search !== caseSearchQueries[patientId]) {
        setCaseSearchQueries((prev) => ({
          ...prev,
          [patientId]: search,
        }));
      }

      // We'd need to update the API to support search here
      const data = await listPatientCases(token!, patientId, caseLimit, offset);

      // If we have search query, filter results client-side
      // (ideally this would be done server-side)
      const filteredData = searchQuery
        ? data.filter(
            (c) =>
              c.caseId.toLowerCase().includes(searchQuery.toLowerCase()) ||
              (c.fileName &&
                c.fileName.toLowerCase().includes(searchQuery.toLowerCase())),
          )
        : data;

      setCasesByPatient((prev) => ({
        ...prev,
        [patientId]: reset
          ? filteredData
          : [...(prev[patientId] ?? []), ...filteredData],
      }));

      setCaseHasMore((prev) => ({
        ...prev,
        [patientId]: data.length === caseLimit,
      }));

      setCaseOffset((prev) => ({ ...prev, [patientId]: offset + data.length }));
    } finally {
      setCasesLoading((prev) => ({ ...prev, [patientId]: false }));
    }
  }
  async function handleCreate(data: {
    givenName: string;
    familyName: string;
    dateOfBirth?: string | null;
    sex?: string | null;
    notes?: string | null;
  }) {
    const token = await authService.getAccessToken();
    if (!token) throw new Error("No access token available");

    // Validate required fields
    if (!data.dateOfBirth) {
      throw new Error("Date of birth is required");
    }

    // Check for duplicate patient names
    const trimmedGivenName = data.givenName.trim().toLowerCase();
    const trimmedFamilyName = data.familyName.trim().toLowerCase();

    const existingPatient = patients.find(
      (p) =>
        p.givenName?.trim().toLowerCase() === trimmedGivenName &&
        p.familyName?.trim().toLowerCase() === trimmedFamilyName,
    );

    if (existingPatient) {
      throw new Error(
        `A patient with the name "${data.givenName.trim()} ${data.familyName.trim()}" already exists`,
      );
    }

    const created = await createPatient(token, {
      givenName: data.givenName,
      familyName: data.familyName,
      dateOfBirth: data.dateOfBirth, // Keep the format as received from the form
      sex: data.sex ?? undefined,
      notes: data.notes ?? undefined,
    });

    // Reset and reload patient data from scratch
    setOffset(0);
    await fetchPatients(undefined, /*reset=*/ true);

    // Also reload orphaned cases in case we want to assign them to the new patient
    fetchOrphanedCases(true);
  }

  function openEditDialog(p: Patient) {
    setEditing(p);
    setEditForm({
      givenName: p.givenName || "",
      familyName: p.familyName || "",
      dateOfBirth: p.dateOfBirth
        ? dayjs(p.dateOfBirth).format("YYYY-MM-DD")
        : "",
      sex: p.sex || "",
      notes: p.notes || "",
    });
    setEditOpen(true);
  }

  async function handleUpdate() {
    if (!editing) return;

    // Clear previous errors
    setEditErrors({
      givenName: "",
      familyName: "",
      general: "",
    });

    // Validate required fields
    const newErrors = {
      givenName: "",
      familyName: "",
      general: "",
    };

    if (!editForm.givenName.trim()) {
      newErrors.givenName = "Given name is required";
    }

    if (!editForm.familyName.trim()) {
      newErrors.familyName = "Family name is required";
    }

    if (newErrors.givenName || newErrors.familyName) {
      setEditErrors(newErrors);
      return;
    }

    try {
      const token = await authService.getAccessToken();
      if (!token) throw new Error("No access token available");

      // Check for duplicate patient names (excluding current patient)
      const trimmedGivenName = editForm.givenName.trim().toLowerCase();
      const trimmedFamilyName = editForm.familyName.trim().toLowerCase();

      const existingPatient = patients.find(
        (p) =>
          p.id !== editing.id && // Exclude current patient
          p.givenName?.trim().toLowerCase() === trimmedGivenName &&
          p.familyName?.trim().toLowerCase() === trimmedFamilyName,
      );

      if (existingPatient) {
        setEditErrors({
          givenName: "",
          familyName: "",
          general: `A patient with the name "${editForm.givenName.trim()} ${editForm.familyName.trim()}" already exists`,
        });
        return;
      }

      const body = {
        givenName: editForm.givenName.trim(),
        familyName: editForm.familyName.trim(),
        dateOfBirth: editForm.dateOfBirth
          ? new Date(editForm.dateOfBirth).toISOString()
          : null,
        sex: editForm.sex || null,
        notes: editForm.notes || null,
      };
      const updated = await updatePatient(token, editing.id, body);
      // update local state
      setPatients((prev) =>
        prev.map((p) => (p.id === editing.id ? updated : p)),
      );
      setEditOpen(false);
      setEditing(null);
      setEditErrors({
        givenName: "",
        familyName: "",
        general: "",
      });
    } catch (error) {
      console.error("Failed to update patient:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      setEditErrors({
        givenName: "",
        familyName: "",
        general: errorMessage,
      });
    }
  }

  async function handleDelete(id: string) {
    const token = await authService.getAccessToken();
    if (!token) throw new Error("No access token available");
    await deletePatient(token, id);
    setPatients((prev) => prev.filter((p) => p.id !== id));
    setDeleteDialogOpen(false);
    setPatientToDelete(null);
  }

  function openDeleteDialog(patient: Patient) {
    setPatientToDelete(patient);
    setDeleteDialogOpen(true);
  }

  // Copy ID to clipboard
  const handleCopyId = (id: string, type: 'patient' | 'case') => {
    navigator.clipboard.writeText(id);
    setCopyMessage(`${type === 'patient' ? 'Patient' : 'Case'} ID copied to clipboard`);
    setCopySnackbarOpen(true);
  };

  // helper (GO TO ChatDomain with new case)
  function goToNewInteraction(
    patientId: string,
    stage: "initial" | "segment" | "followup" | "xai" = "initial",
  ) {
    // uuid path param = patientId; also pass query so ChatDomain knows which step to open
    navigate(`/ChatDomain/${patientId}?stage=${stage}&patientId=${patientId}`);
  }
  //helpers Expand CASES here
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [casesByPatient, setCasesByPatient] = useState<Record<string, any[]>>(
    {},
  );
  const [casesLoading, setCasesLoading] = useState<Record<string, boolean>>({});

  async function toggleExpand(p: Patient) {
    const pid = p.patientId;
    const newVal = !expanded[pid];
    setExpanded((prev) => ({ ...prev, [pid]: newVal }));

    // lazy-load cases on first expand
    if (newVal && !casesByPatient[pid]) {
      setCasesLoading((prev) => ({ ...prev, [pid]: true }));
      try {
        const token = await authService.getAccessToken();
        if (!token) throw new Error("No access token available");
        const cases = await listPatientCases(token, pid);
        setCasesByPatient((prev) => ({ ...prev, [pid]: cases }));
      } finally {
        setCasesLoading((prev) => ({ ...prev, [pid]: false }));
      }
    }
  }

  function openChatForPatient(
    patientId: string,
    stage: "initial" | "segment" | "followup" | "xai" = "initial",
    caseId?: string,
  ) {
    const qp = new URLSearchParams({ stage, patientId });
    if (caseId) qp.set("caseId", caseId);
    qp.set("tab", "details");
    navigate(`/cases/${caseId}/details?${qp.toString()}`);
  }

  // Fetch orphaned cases
  async function fetchOrphanedCases(reset = false, search?: string) {
    setOrphanedCasesLoading(true);
    try {
      const token = await authService.getAccessToken();
      if (!token) throw new Error("No access token available");

      // If search is provided, use it, otherwise use the existing search term
      const searchTerm = search !== undefined ? search : orphanedCasesSearch;

      // If resetting or changing search, start from beginning
      let nextOffset = reset ? 0 : orphanedCasesOffset;

      // If search is being changed, update the state
      if (search !== undefined && search !== orphanedCasesSearch) {
        setOrphanedCasesSearch(search);
      }

      // Keep fetching batches until we have enough orphaned cases or run out of data
      const allOrphaned: any[] = [];
      const minOrphanedCases = reset ? 10 : 5; // Minimum we want to fetch
      let hasMoreData = true;
      let fetchCount = 0;
      const maxFetches = 5; // Prevent infinite loops

      while (
        allOrphaned.length < minOrphanedCases && 
        hasMoreData && 
        fetchCount < maxFetches
      ) {
        const data = await listUserCases(
          token,
          searchTerm,
          orphanedCasesLimit,
          nextOffset,
        );
        
        const orphaned = data.filter((c: any) => !c.patientId);
        allOrphaned.push(...orphaned);
        
        // Update offset for next iteration
        nextOffset += data.length;
        fetchCount++;
        
        // Check if we've reached the end
        hasMoreData = data.length === orphanedCasesLimit;
        
        // If we got no orphaned cases in this batch and still have data, keep going
        // Otherwise break if we got fewer cases than the limit (end of data)
        if (data.length < orphanedCasesLimit) {
          hasMoreData = false;
        }
      }

      // Update state
      setOrphanedCases((prev) => (reset ? allOrphaned : [...prev, ...allOrphaned]));
      setHasMoreOrphanedCases(hasMoreData);
      setOrphanedCasesOffset(nextOffset);
      
    } finally {
      setOrphanedCasesLoading(false);
    }
  }
  // open dialog to edit (assign) an orphaned case
  const [caseEditOpen, setCaseEditOpen] = useState(false);
  const [editingCase, setEditingCase] = useState<any>(null);
  const [caseEditForm, setCaseEditForm] = useState({
    patientId: "",
    patientSearchQuery: "",
  });
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [isLoadingPatientSearch, setIsLoadingPatientSearch] = useState(false);

  function openCaseEditDialog(c: any) {
    setEditingCase(c);
    setCaseEditForm({
      patientId: c.patientId || "",
      patientSearchQuery: "",
    });
    setCaseEditOpen(true);
    // Initialize filtered patients with all patients or empty if we need to search
    setFilteredPatients(patients.slice(0, 10)); // Show first 10 by default
  }

  async function searchPatientsForCaseAssignment(query: string) {
    setIsLoadingPatientSearch(true);
    try {
      // If query is empty, just use the first few patients we already have
      if (!query.trim()) {
        setFilteredPatients(patients.slice(0, 10));
        return;
      }

      // Otherwise, search patients with the query
      const token = await authService.getAccessToken();
      if (!token) throw new Error("No access token available");

      const searchResults = await listPatients(token, query, 10, 0);
      setFilteredPatients(searchResults);
    } catch (error) {
      console.error("Error searching patients:", error);
      // Fallback to local filtering if API search fails
      const lowercaseQuery = query.toLowerCase();
      const localFiltered = patients
        .filter(
          (p) =>
            p.givenName?.toLowerCase().includes(lowercaseQuery) ||
            p.familyName?.toLowerCase().includes(lowercaseQuery) ||
            p.patientId?.toLowerCase().includes(lowercaseQuery),
        )
        .slice(0, 10);
      setFilteredPatients(localFiltered);
    } finally {
      setIsLoadingPatientSearch(false);
    }
  }

  // Helper to reload all visible case data
  async function reloadAllCaseData() {
    // Reload orphaned cases
    fetchOrphanedCases(true);

    // Reload any expanded patient cases
    for (const patientId of Object.keys(expanded)) {
      if (expanded[patientId] && casesByPatient[patientId]) {
        loadCases(patientId, true);
      }
    }
  }

  async function handleCaseUpdate() {
    if (!editingCase) return;
    try {
      const token = await authService.getAccessToken();
      if (!token) throw new Error("No access token available");

      // Track patientIds for UI updates
      const previousPatientId = editingCase.patientId;
      const newPatientId = caseEditForm.patientId || undefined;

      console.log(
        `Moving case ${editingCase.caseId} from ${previousPatientId || "orphaned"} to ${newPatientId || "orphaned"}`,
      );

      // Make API call to update the case
      await updateCase(token, editingCase.caseId, {
        patientId: newPatientId,
      });

      // Update all associated reports with the new patientId
      try {
        const reports = await getReportsByCase(token, editingCase.caseId);
        console.log(`Found ${reports.length} reports for case ${editingCase.caseId}`);
        
        // Update each report with the new patientId
        for (const report of reports) {
          await updateReport(token, report.id!, {
            ...report,
            patientId: newPatientId || "",
          });
          console.log(`Updated report ${report.id} with patientId: ${newPatientId || "orphaned"}`);
        }
        
        if (reports.length > 0) {
          console.log(`✅ Successfully updated ${reports.length} associated report(s)`);
        }
      } catch (reportError) {
        console.error("Error updating associated reports:", reportError);
        // Don't fail the entire operation if report updates fail
      }

      // Reload all case data to ensure UI consistency
      await reloadAllCaseData();

      // Close dialog and reset state
      setCaseEditOpen(false);
      setEditingCase(null);
    } catch (error) {
      console.error("Failed to update case:", error);
    }
  }

  // Define tour steps for the patient space
  const tourSteps = useMemo(() => {
    const baseSteps = [
      {
        target: '[data-tour="patient-search"]',
        content: patients.length
          ? "Search for patients by name or patient ID here"
          : "Once you have patients, you can search them here by name or patient ID",
        disableBeacon: true,
      },
      {
        target: '[data-tour="new-patient"]',
        content: patients.length
          ? "Create a new patient record with this button"
          : "Start by creating your first patient record",
        disableBeacon: true,
      },
      ...(patients.length
        ? [
            {
              target: '[data-tour="patient-actions"]',
              content:
                "Add cases (🗂️ opens the AI assistant), edit patient details, or delete patient records",
              disableBeacon: true,
            },
            {
              target: '[data-tour="patient-expand"]',
              content:
                "Click this arrow to view cases associated with a patient",
              disableBeacon: true,
            },
          ]
        : []),
      {
        target: '[data-tour="orphaned-cases"]',
        content: patients.length
          ? "View and manage cases that are not assigned to any patient"
          : "Any cases you create before assigning a patient will appear here",
        disableBeacon: true,
      },
    ];
    return baseSteps;
  }, [patients]);

  const handleStartTour = (e: React.MouseEvent) => {
    e.preventDefault();
    // Reset the tour completion status in localStorage
    localStorage.removeItem("tour_patient-space_completed");
    localStorage.removeItem("tour_patient-space_step");
    // Force tour to stop and then start again (important for restarting)
    setRunTour(false);
    // Use setTimeout to ensure React has time to process the false state before setting to true
    setTimeout(() => setRunTour(true), 50);
  };

  const handleFinishTour = () => {
    setRunTour(false);
  };

  // Bottom sheet handlers
  const openActionSheet = (patient: Patient) => {
    setSelectedPatient(patient);
    setActionSheetOpen(true);
  };

  const closeActionSheet = () => {
    setActionSheetOpen(false);
    setTimeout(() => setSelectedPatient(null), 200);
  };

  const handleActionAndClose = (action: () => void) => {
    action();
    closeActionSheet();
  };

  const openCaseActionSheet = (caseItem: any) => {
    setSelectedCase(caseItem);
    setCaseActionSheetOpen(true);
  };

  const closeCaseActionSheet = () => {
    setCaseActionSheetOpen(false);
    setTimeout(() => setSelectedCase(null), 200);
  };

  // Calculate age from DOB
  const calculateAge = (dob: string) => {
    const years = dayjs().diff(dayjs(dob), 'year');
    return years > 0 ? `${years}y` : '';
  };

  return (
    // <>
    //   {!isAdmin ? (
    //     <p>REsticted</p>
    //   ) : (
        <Box sx={{ p: { xs: 1, md: 2 } }}>
          <Card 
            elevation={2} 
            sx={{ 
              borderRadius: 4,
              background: isDarkMode 
                ? 'linear-gradient(145deg, rgba(30,30,35,0.95) 0%, rgba(25,25,30,0.98) 100%)'
                : 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(250,250,252,0.98) 100%)',
              backdropFilter: 'blur(20px)',
              boxShadow: isDarkMode
                ? '0 20px 60px rgba(0,0,0,0.5), 0 0 1px rgba(255,255,255,0.1) inset'
                : '0 20px 60px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.05) inset',
            }}
          >
            <CardHeader
              title={
                <Typography 
                  variant="h5" 
                  fontWeight={700}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Patients
                </Typography>
              }
              subheader="Create, search, edit and delete patient records"
              action={
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Refresh">
                    <IconButton
                      onClick={() => fetchPatients(q)}
                      disabled={loading}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Button
                    onClick={() => setOpen(true)}
                    startIcon={!isMobile ? <AddIcon /> : undefined}
                    variant="contained"
                    data-tour="new-patient"
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      minWidth: isMobile ? 40 : 'auto',
                      px: isMobile ? 1 : 2,
                    }}
                  >
                    {isMobile ? <AddIcon /> : 'New Patient'}
                  </Button>
                  {isMobile?<></>:<Button
                    onClick={handleStartTour}
                    startIcon={!isMobile ? <SchoolIcon /> : undefined}
                    variant="contained"
                    color="secondary"
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      backgroundColor: "#e91e63",
                      color: "#ffffff",
                      minWidth: isMobile ? 40 : 'auto',
                      px: isMobile ? 1 : 2,
                      "&:hover": {
                        backgroundColor: "#d81b60",
                      },
                    }}
                  >Guide
                  </Button>}
                  
                </Stack>
              }
            />
            <CardContent>
              <Stack spacing={2}>
                <TextField
                  placeholder="Search by patient ID, given/family name…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") fetchPatients(q);
                  }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: alpha(theme.palette.background.paper, 0.6),
                      backdropFilter: 'blur(8px)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.background.paper, 0.8),
                      },
                      '&.Mui-focused': {
                        bgcolor: theme.palette.background.paper,
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: q ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setQ("");
                            fetchPatients("", true);
                          }}
                          aria-label="clear search"
                        >
                          <CloseIcon />
                        </IconButton>
                        <Button
                          onClick={() => fetchPatients(q, true)}
                          disabled={loading}
                        >
                          Search
                        </Button>
                      </InputAdornment>
                    ) : (
                      <Button
                        onClick={() => fetchPatients(q, true)}
                        disabled={loading}
                      >
                        Search
                      </Button>
                    ),
                  }}
                  data-tour="patient-search"
                />
                
                {/* Unified Card View for Mobile and Desktop */}
                <Stack spacing={isMobile ? 2 : 3}>
                  {rows.map((r) => {
                    const isOpen = !!expanded[r.patientId];
                    const cases = casesByPatient[r.patientId] || [];
                    const isCasesLoading = !!casesLoading[r.patientId];
                    const age = r.dateOfBirth ? calculateAge(r.dateOfBirth) : null;

                    return (
                      <Card
                        key={r.id}
                        elevation={isMobile ? 2 : 3}
                        sx={{
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          borderRadius: isMobile ? 3 : 4,
                          background: alpha(theme.palette.background.paper, 0.6),
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.2s',
                          '&:hover': {
                            transform: isMobile ? 'none' : 'translateY(-2px)',
                            boxShadow: isMobile ? undefined : `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                          },
                          '&:active': {
                            transform: 'scale(0.98)',
                          }
                        }}
                      >
                        <CardContent sx={{ p: isMobile ? 2 : 3, '&:last-child': { pb: isMobile ? 2 : 3 } }}>
                          {/* Header Row */}
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'flex-start', 
                              gap: isMobile ? 2 : 3,
                              mb: isMobile ? 1.5 : 2
                            }}
                            onClick={isMobile ? () => openActionSheet(r) : undefined}
                          >
                            <Avatar
                              sx={{
                                width: isMobile ? 48 : 64,
                                height: isMobile ? 48 : 64,
                                bgcolor: alpha(theme.palette.primary.main, 0.15),
                                color: theme.palette.primary.main,
                                fontWeight: 700,
                                fontSize: isMobile ? '1.1rem' : '1.5rem',
                              }}
                            >
                              {r.givenName?.[0]}{r.familyName?.[0]}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography 
                                variant={isMobile ? "h6" : "h5"}
                                sx={{ 
                                  fontWeight: 700,
                                  fontSize: isMobile ? '1.1rem' : '1.5rem',
                                  mb: isMobile ? 0.5 : 1,
                                  lineHeight: 1.2
                                }}
                              >
                                {r.givenName} {r.familyName}
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" gap={isMobile ? 0.5 : 1}>
                                {age && (
                                  <Chip 
                                    icon={<CakeIcon />}
                                    label={age}
                                    size={isMobile ? "small" : "medium"}
                                    variant="outlined"
                                    sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                                  />
                                )}
                                {r.sex && (
                                  <Chip 
                                    icon={<WcIcon />}
                                    label={r.sex}
                                    size={isMobile ? "small" : "medium"}
                                    variant="outlined"
                                    sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                                  />
                                )}
                                {cases.length > 0 && (
                                  <Chip 
                                    icon={<FolderIcon />}
                                    label={`${cases.length} case${cases.length !== 1 ? 's' : ''}`}
                                    size={isMobile ? "small" : "medium"}
                                    color="primary"
                                    variant="filled"
                                    sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                                  />
                                )}
                                {r.createdAt && !isMobile && (
                                  <Chip 
                                    label={dayjs(r.createdAt).format("MMM D, YYYY")}
                                    size="medium"
                                    variant="outlined"
                                    sx={{ fontSize: '0.875rem', color: 'text.secondary' }}
                                  />
                                )}
                              </Stack>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              {!isMobile && (
                                <Box sx={{ display: 'flex', gap: 0.5 }} data-tour={r === rows[0] ? "patient-actions" : undefined}>
                                  <Tooltip title="Add Case / New Interaction" arrow>
                                    <IconButton
                                      onClick={() => goToNewInteraction(r.patientId, "initial")}
                                      size="medium"
                                      sx={{
                                        color: theme.palette.success.main,
                                        bgcolor: alpha(theme.palette.success.main, 0.08),
                                        '&:hover': {
                                          bgcolor: alpha(theme.palette.success.main, 0.15),
                                          transform: 'scale(1.1)',
                                        },
                                        transition: 'all 0.2s',
                                      }}
                                    >
                                      <AddCircleOutlineIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Edit" arrow>
                                    <IconButton 
                                      onClick={() => openEditDialog(r)}
                                      size="medium"
                                      sx={{
                                        color: theme.palette.primary.main,
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        '&:hover': {
                                          bgcolor: alpha(theme.palette.primary.main, 0.15),
                                          transform: 'scale(1.1)',
                                        },
                                        transition: 'all 0.2s',
                                      }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete" arrow>
                                    <IconButton
                                      onClick={() => openDeleteDialog(r)}
                                      size="medium"
                                      sx={{
                                        color: theme.palette.error.main,
                                        bgcolor: alpha(theme.palette.error.main, 0.08),
                                        '&:hover': {
                                          bgcolor: alpha(theme.palette.error.main, 0.15),
                                          transform: 'scale(1.1)',
                                        },
                                        transition: 'all 0.2s',
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                              <IconButton
                                size={isMobile ? "small" : "medium"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpand(r);
                                }}
                                data-tour={r === rows[0] ? "patient-expand" : undefined}
                                sx={{
                                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                                  }
                                }}
                              >
                                {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Box>
                          </Box>

                          {/* Patient ID */}
                          <Chip
                            label={`ID: ${r.patientId.substring(0, isMobile ? 12 : 20)}...`}
                            size={isMobile ? "small" : "medium"}
                            variant="outlined"
                            icon={<ContentCopyIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyId(r.patientId, 'patient');
                            }}
                            sx={{ 
                              fontFamily: 'monospace',
                              fontSize: isMobile ? '0.7rem' : '0.875rem',
                              height: isMobile ? 24 : 32,
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                borderColor: theme.palette.primary.main,
                              },
                              '& .MuiChip-icon': {
                                fontSize: isMobile ? '0.75rem' : '1rem',
                              }
                            }}
                          />

                          {/* Expanded Cases */}
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: isMobile ? 2 : 3, pt: isMobile ? 2 : 3, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                              {/* Case Search - Desktop Only */}
                              {!isMobile && (
                                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="h6" fontWeight={600}>
                                    Previous Cases
                                  </Typography>
                                  <TextField
                                    placeholder="Search cases..."
                                    size="small"
                                    value={caseSearchQueries[r.patientId] || ""}
                                    onChange={(e) => {
                                      const searchValue = e.target.value;
                                      setCaseSearchQueries((prev) => ({
                                        ...prev,
                                        [r.patientId]: searchValue,
                                      }));
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        loadCases(r.patientId, true, caseSearchQueries[r.patientId]);
                                      }
                                    }}
                                    sx={{ width: '300px' }}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                      ),
                                      endAdornment: caseSearchQueries[r.patientId] ? (
                                        <InputAdornment position="end">
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              setCaseSearchQueries((prev) => ({
                                                ...prev,
                                                [r.patientId]: "",
                                              }));
                                              loadCases(r.patientId, true, "");
                                            }}
                                          >
                                            <CloseIcon fontSize="small" />
                                          </IconButton>
                                          <Button
                                            size="small"
                                            onClick={() => loadCases(r.patientId, true, caseSearchQueries[r.patientId])}
                                            disabled={casesLoading[r.patientId]}
                                          >
                                            Search
                                          </Button>
                                        </InputAdornment>
                                      ) : (
                                        <Button
                                          size="small"
                                          onClick={() => loadCases(r.patientId, true, caseSearchQueries[r.patientId])}
                                          disabled={casesLoading[r.patientId]}
                                        >
                                          Search
                                        </Button>
                                      ),
                                    }}
                                  />
                                </Box>
                              )}
                              
                              {isCasesLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: isMobile ? 2 : 3 }}>
                                  <CircularProgress size={isMobile ? 24 : 32} />
                                </Box>
                              ) : cases.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: isMobile ? 2 : 3, fontSize: isMobile ? '0.875rem' : '1rem' }}>
                                  No cases yet
                                </Typography>
                              ) : (
                                <Stack spacing={isMobile ? 1 : 1.5}>
                                  {cases.map((c: any) => (
                                    <Card
                                      key={c.id || c.caseId}
                                      variant="outlined"
                                      sx={{
                                        borderRadius: isMobile ? 2 : 3,
                                        bgcolor: alpha(theme.palette.background.default, 0.5),
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                          bgcolor: alpha(theme.palette.background.default, 0.7),
                                          borderColor: theme.palette.primary.main,
                                        }
                                      }}
                                    >
                                      <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={isMobile ? 1 : 2}>
                                          <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Chip
                                              label={`${c.caseId?.substring(0, isMobile ? 18 : 28) || 'N/A'}...`}
                                              size={isMobile ? "small" : "medium"}
                                              icon={<ContentCopyIcon />}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                c.caseId && handleCopyId(c.caseId, 'case');
                                              }}
                                              sx={{
                                                fontFamily: 'monospace',
                                                fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                height: isMobile ? 26 : 32,
                                                mb: c.fileName ? 0.75 : 0,
                                                cursor: 'pointer',
                                                '&:hover': {
                                                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                                                },
                                                '& .MuiChip-icon': {
                                                  fontSize: isMobile ? '0.75rem' : '1rem',
                                                }
                                              }}
                                            />
                                            {c.fileName && (
                                              <Typography variant="body2" sx={{ fontSize: isMobile ? '0.825rem' : '0.95rem', color: 'text.secondary', mt: 0.5 }}>
                                                {c.fileName}
                                              </Typography>
                                            )}
                                            {!isMobile && c.createdAt && (
                                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.disabled' }}>
                                                Created: {dayjs(c.createdAt).format("MMM D, YYYY HH:mm")}
                                              </Typography>
                                            )}
                                          </Box>
                                          <Box sx={{ display: 'flex', gap: isMobile ? 0 : 0.5 }}>
                                            {!isMobile && (
                                              <Tooltip title="Edit Case" arrow>
                                                <IconButton
                                                  onClick={() => openCaseEditDialog(c)}
                                                  size="medium"
                                                  sx={{
                                                    color: theme.palette.warning.main,
                                                    bgcolor: alpha(theme.palette.warning.main, 0.08),
                                                    '&:hover': {
                                                      bgcolor: alpha(theme.palette.warning.main, 0.15),
                                                      transform: 'scale(1.1)',
                                                    },
                                                    transition: 'all 0.2s',
                                                  }}
                                                >
                                                  <EditIcon />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                            <Tooltip title={isMobile ? "View" : "Open chat"} arrow>
                                              <IconButton 
                                                size={isMobile ? "small" : "medium"}
                                                onClick={() => openChatForPatient(r.patientId, "initial", c.caseId)}
                                                sx={{
                                                  color: theme.palette.primary.main,
                                                  bgcolor: isMobile ? 'transparent' : alpha(theme.palette.info.main, 0.08),
                                                  '&:hover': {
                                                    bgcolor: isMobile ? 'transparent' : alpha(theme.palette.info.main, 0.15),
                                                    transform: 'scale(1.1)',
                                                  },
                                                  transition: 'all 0.2s',
                                                }}
                                              >
                                                {isMobile ? <VisibilityIcon fontSize="small" /> : <ChatIcon />}
                                              </IconButton>
                                            </Tooltip>
                                          </Box>
                                        </Stack>
                                      </CardContent>
                                    </Card>
                                  ))}
                                  {caseHasMore[r.patientId] && (
                                    <Button
                                      onClick={() => loadCases(r.patientId)}
                                      size={isMobile ? "small" : "medium"}
                                      variant="outlined"
                                      fullWidth
                                      sx={{ 
                                        mt: 1,
                                        borderRadius: isMobile ? 2 : 3,
                                        py: isMobile ? 1 : 1.5,
                                        fontWeight: 600,
                                        borderWidth: 2,
                                        '&:hover': {
                                          borderWidth: 2,
                                          transform: 'translateY(-2px)',
                                        }
                                      }}
                                    >
                                      Load More Cases
                                    </Button>
                                  )}
                                </Stack>
                              )}
                            </Box>
                          </Collapse>
                        </CardContent>
                      </Card>
                    );
                  })}

                  {!loading && rows.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: isMobile ? 6 : 8 }}>
                      <PersonIcon sx={{ fontSize: isMobile ? 64 : 96, color: 'text.disabled', mb: 2 }} />
                      <Typography color="text.secondary" variant={isMobile ? "body1" : "h6"}>
                        No patients yet.
                      </Typography>
                    </Box>
                  )}
                  
                  {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: isMobile ? 4 : 6 }}>
                      <CircularProgress size={isMobile ? 40 : 48} />
                    </Box>
                  )}
                </Stack>
                
                {/* Load More Patients */}
                {hasMore && !loading && (
                  <Button
                    onClick={() => fetchPatients()}
                    variant="outlined"
                    size="large"
                    fullWidth={isMobile}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      mt: 3,
                      fontWeight: 600,
                      textTransform: 'none',
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Load More Patients
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Orphaned Cases Card */}
          <Card
            elevation={2}
            sx={{ 
              borderRadius: 4, 
              mt: 3,
              background: isDarkMode 
                ? 'linear-gradient(145deg, rgba(30,30,35,0.95) 0%, rgba(25,25,30,0.98) 100%)'
                : 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(250,250,252,0.98) 100%)',
              backdropFilter: 'blur(20px)',
              boxShadow: isDarkMode
                ? '0 20px 60px rgba(0,0,0,0.5), 0 0 1px rgba(255,255,255,0.1) inset'
                : '0 20px 60px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.05) inset',
            }}
            data-tour="orphaned-cases"
          >
            <CardHeader
              title={
                <Typography 
                  variant="h5" 
                  fontWeight={700}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Orphaned Cases
                </Typography>
              }
              subheader="Cases without associated patients"
              action={
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Refresh">
                    <IconButton
                      onClick={() => fetchOrphanedCases(true)}
                      disabled={orphanedCasesLoading}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
            />
            <CardContent>
              <TextField
                placeholder="Search orphaned cases by ID or filename..."
                value={orphanedCasesSearch}
                onChange={(e) => setOrphanedCasesSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    fetchOrphanedCases(true, orphanedCasesSearch);
                  }
                }}
                fullWidth
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.background.paper, 0.6),
                    backdropFilter: 'blur(8px)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.background.paper, 0.8),
                    },
                    '&.Mui-focused': {
                      bgcolor: theme.palette.background.paper,
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.secondary.main, 0.2)}`,
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: orphanedCasesSearch ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setOrphanedCasesSearch("");
                          fetchOrphanedCases(true, "");
                        }}
                        aria-label="clear search"
                      >
                        <CloseIcon />
                      </IconButton>
                      <Button
                        onClick={() =>
                          fetchOrphanedCases(true, orphanedCasesSearch)
                        }
                        disabled={orphanedCasesLoading}
                      >
                        Search
                      </Button>
                    </InputAdornment>
                  ) : (
                    <Button
                      onClick={() =>
                        fetchOrphanedCases(true, orphanedCasesSearch)
                      }
                      disabled={orphanedCasesLoading}
                    >
                      Search
                    </Button>
                  ),
                }}
              />

              {/* Unified Card View for Orphaned Cases */}
              <Stack spacing={isMobile ? 2 : 3}>
                {orphanedCases.map((c) => (
                  <Card
                    key={c.id || c.caseId}
                    elevation={isMobile ? 2 : 3}
                    sx={{
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      borderRadius: isMobile ? 3 : 4,
                      background: alpha(theme.palette.background.paper, 0.6),
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: isMobile ? 'none' : 'translateY(-2px)',
                        boxShadow: isMobile ? undefined : `0 12px 40px ${alpha(theme.palette.secondary.main, 0.15)}`,
                      },
                      '&:active': {
                        transform: 'scale(0.98)',
                      }
                    }}
                    onClick={isMobile ? () => openCaseActionSheet(c) : undefined}
                  >
                    <CardContent sx={{ p: isMobile ? 2 : 3, '&:last-child': { pb: isMobile ? 2 : 3 } }}>
                      <Stack spacing={isMobile ? 1.5 : 2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                          <Stack spacing={isMobile ? 1.5 : 2} sx={{ flex: 1 }}>
                            {/* Case ID with Copy Feature */}
                            <Chip
                              label={`${c.caseId?.substring(0, isMobile ? 20 : 32) || 'N/A'}...`}
                              size={isMobile ? "small" : "medium"}
                              variant="outlined"
                              icon={<ContentCopyIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                c.caseId && handleCopyId(c.caseId, 'case');
                              }}
                              sx={{ 
                                fontFamily: 'monospace',
                                fontSize: isMobile ? '0.75rem' : '0.95rem',
                                height: isMobile ? 28 : 36,
                                cursor: 'pointer',
                                alignSelf: 'flex-start',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.secondary.main, 0.08),
                                  borderColor: theme.palette.secondary.main,
                                },
                                '& .MuiChip-icon': {
                                  fontSize: isMobile ? '0.875rem' : '1.1rem',
                                }
                              }}
                            />

                            {/* File Name (if exists) */}
                            {c.fileName && (
                              <Typography variant="body1" sx={{ fontSize: isMobile ? '0.875rem' : '1.1rem', fontWeight: 600 }}>
                                {c.fileName}
                              </Typography>
                            )}

                            {/* Metadata Row */}
                            <Stack direction="row" spacing={1} flexWrap="wrap" gap={isMobile ? 0.5 : 1}>
                              {(c.mimeType || c.contentType) && (
                                <Chip 
                                  label={c.mimeType || c.contentType}
                                  size={isMobile ? "small" : "medium"}
                                  variant="outlined"
                                  sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                                />
                              )}
                              {c.createdAt && (
                                <Chip 
                                  icon={<CakeIcon />}
                                  label={dayjs(c.createdAt).format(isMobile ? "MMM D, YYYY" : "MMM D, YYYY HH:mm")}
                                  size={isMobile ? "small" : "medium"}
                                  variant="outlined"
                                  sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                                />
                              )}
                            </Stack>
                          </Stack>
                          
                          {/* Desktop Actions */}
                          {!isMobile && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Assign to Patient" arrow>
                                <IconButton 
                                  onClick={() => openCaseEditDialog(c)}
                                  size="medium"
                                  sx={{
                                    color: theme.palette.warning.main,
                                    bgcolor: alpha(theme.palette.warning.main, 0.08),
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.warning.main, 0.15),
                                      transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View Case" arrow>
                                <IconButton
                                  onClick={() => navigate(`/cases/${c.caseId}/details?tab=details`)}
                                  size="medium"
                                  sx={{
                                    color: theme.palette.info.main,
                                    bgcolor: alpha(theme.palette.info.main, 0.08),
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.info.main, 0.15),
                                      transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <ChatIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}

                {!orphanedCasesLoading && orphanedCases.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: isMobile ? 6 : 8 }}>
                    <FolderIcon sx={{ fontSize: isMobile ? 64 : 96, color: 'text.disabled', mb: 2 }} />
                    <Typography color="text.secondary" variant={isMobile ? "body1" : "h6"}>
                      No orphaned cases found.
                    </Typography>
                  </Box>
                )}
                
                {orphanedCasesLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: isMobile ? 4 : 6 }}>
                    <CircularProgress size={isMobile ? 40 : 48} />
                  </Box>
                )}
              </Stack>

              {/* Load More Button */}
              {hasMoreOrphanedCases && !orphanedCasesLoading && (
                <Button
                  onClick={() => fetchOrphanedCases()}
                  variant="outlined"
                  size="large"
                  fullWidth={isMobile}
                  sx={{
                    mt: 3,
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.2)}`,
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Load More Cases
                </Button>
              )}
            </CardContent>
          </Card>

          <PatientFormDialog
            open={open}
            onClose={() => setOpen(false)}
            onSubmit={handleCreate}
          />

          {/* Edit Dialog */}
          <Dialog
            open={editOpen}
            onClose={() => {
              setEditOpen(false);
              setEditErrors({
                givenName: "",
                familyName: "",
                general: "",
              });
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Edit Patient</DialogTitle>
            <DialogContent>
              <Stack spacing={2} mt={2}>
                {editErrors.general && (
                  <Alert
                    severity="error"
                    icon={<ErrorIcon />}
                    sx={{
                      borderRadius: 2,
                      "& .MuiAlert-message": {
                        fontWeight: 500,
                      },
                      "& .MuiAlert-icon": {
                        color: "error.main",
                      },
                      backgroundColor: "#ffebee",
                      borderLeft: "4px solid",
                      borderLeftColor: "error.main",
                      boxShadow: "0 2px 8px rgba(244, 67, 54, 0.15)",
                    }}
                  >
                    {editErrors.general}
                  </Alert>
                )}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Given Name"
                    value={editForm.givenName}
                    onChange={(e) => {
                      setEditForm({ ...editForm, givenName: e.target.value });
                      if (editErrors.givenName) {
                        setEditErrors({ ...editErrors, givenName: "" });
                      }
                    }}
                    required
                    fullWidth
                    error={!!editErrors.givenName}
                    helperText={editErrors.givenName}
                    sx={{
                      "& .MuiFormHelperText-root.Mui-error": {
                        fontWeight: 500,
                        fontSize: "0.875rem",
                      },
                    }}
                  />
                  <TextField
                    label="Family Name"
                    value={editForm.familyName}
                    onChange={(e) => {
                      setEditForm({ ...editForm, familyName: e.target.value });
                      if (editErrors.familyName) {
                        setEditErrors({ ...editErrors, familyName: "" });
                      }
                    }}
                    required
                    fullWidth
                    error={!!editErrors.familyName}
                    helperText={editErrors.familyName}
                    sx={{
                      "& .MuiFormHelperText-root.Mui-error": {
                        fontWeight: 500,
                        fontSize: "0.875rem",
                      },
                    }}
                  />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Date of Birth"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={editForm.dateOfBirth}
                    onChange={(e) =>
                      setEditForm({ ...editForm, dateOfBirth: e.target.value })
                    }
                    fullWidth
                  />
                  <TextField
                    select
                    label="Sex"
                    value={editForm.sex}
                    onChange={(e) =>
                      setEditForm({ ...editForm, sex: e.target.value })
                    }
                    fullWidth
                  >
                    <MenuItem value="">—</MenuItem>
                    {sexes.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <TextField
                  label="Notes"
                  multiline
                  minRows={3}
                  value={editForm.notes}
                  onChange={(e) =>
                    setEditForm({ ...editForm, notes: e.target.value })
                  }
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setEditOpen(false);
                  setEditErrors({
                    givenName: "",
                    familyName: "",
                    general: "",
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleUpdate}
                disabled={
                  !editForm.givenName.trim() || !editForm.familyName.trim()
                }
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Orphaned Case Edit Dialog with Patient Search */}
          <Dialog
            open={caseEditOpen}
            onClose={() => setCaseEditOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {caseEditForm.patientId
                ? "Reassign Case"
                : "Assign Case to Patient"}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} mt={2}>
                <Typography variant="body2" color="text.secondary">
                  Case ID: {editingCase?.caseId || ""}
                </Typography>
                {editingCase?.fileName && (
                  <Typography variant="body2" color="text.secondary">
                    File: {editingCase.fileName}
                  </Typography>
                )}

                <TextField
                  label="Search Patients"
                  placeholder="Type to search patients by name or ID"
                  value={caseEditForm.patientSearchQuery}
                  onChange={(e) => {
                    const query = e.target.value;
                    setCaseEditForm({
                      ...caseEditForm,
                      patientSearchQuery: query,
                    });
                    searchPatientsForCaseAssignment(query);
                  }}
                  fullWidth
                  InputProps={{
                    endAdornment: caseEditForm.patientSearchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setCaseEditForm({
                              ...caseEditForm,
                              patientSearchQuery: "",
                            });
                            setFilteredPatients(patients.slice(0, 10));
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                {isLoadingPatientSearch && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}

                <FormControl fullWidth>
                  <InputLabel>Select Patient</InputLabel>
                  <Select
                    value={caseEditForm.patientId}
                    onChange={(e) =>
                      setCaseEditForm({
                        ...caseEditForm,
                        patientId: e.target.value,
                      })
                    }
                    label="Select Patient"
                  >
                    <MenuItem value="">
                      <em>None (Unassigned case)</em>
                    </MenuItem>
                    {filteredPatients.map((p) => (
                      <MenuItem key={p.id} value={p.patientId}>
                        {p.givenName} {p.familyName} ({p.patientId})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {editingCase?.patientId &&
                  editingCase.patientId !== caseEditForm.patientId && (
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                      This will remove the case from its current patient.
                    </Alert>
                  )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCaseEditOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleCaseUpdate}
                color={
                  editingCase?.patientId !== caseEditForm.patientId
                    ? "warning"
                    : "primary"
                }
              >
                {!editingCase?.patientId && caseEditForm.patientId
                  ? "Assign"
                  : editingCase?.patientId && !caseEditForm.patientId
                    ? "Unassign"
                    : "Save Changes"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add Tour Guide component */}
          <TourGuide
          steps={tourSteps}
            tourId="patient-space"
            run={runTour}
            continuous={true}
            showSkipButton={true}
            showProgress={true}
            onFinish={handleFinishTour}
            onSkip={handleFinishTour}
          />

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirm Delete Patient</DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  Are you sure you want to delete this patient?
                </Alert>
                {patientToDelete && (
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      {patientToDelete.givenName} {patientToDelete.familyName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Patient ID: {patientToDelete.patientId}
                    </Typography>
                    {patientToDelete.dateOfBirth && (
                      <Typography variant="body2" color="text.secondary">
                        DOB: {dayjs(patientToDelete.dateOfBirth).format("YYYY-MM-DD")}
                      </Typography>
                    )}
                  </Box>
                )}
                <Typography variant="body2" color="error">
                  This action cannot be undone. Any cases associated with this patient will become orphaned.
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => patientToDelete && handleDelete(patientToDelete.id)}
              >
                Delete Patient
              </Button>
            </DialogActions>
          </Dialog>

          {/* Copy Notification Snackbar */}
          <Snackbar 
            open={copySnackbarOpen} 
            autoHideDuration={2000}
            onClose={() => setCopySnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => setCopySnackbarOpen(false)} 
              severity="success"
              sx={{ 
                width: '100%',
                borderRadius: 2,
                fontWeight: 600,
              }}
              icon={<ContentCopyIcon />}
            >
              {copyMessage}
            </Alert>
          </Snackbar>

          {/* Patient Actions Bottom Sheet (Mobile) */}
          <Drawer
            anchor="bottom"
            open={actionSheetOpen}
            onClose={closeActionSheet}
            PaperProps={{
              sx: {
                borderTopLeftRadius: 24,
                borderTopRightRadius: 24,
                maxHeight: '70vh',
                pb: 3,
              }
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
              {/* Drag Handle */}
              <Box
                sx={{
                  height: 4,
                  width: 40,
                  bgcolor: 'grey.300',
                  borderRadius: 2,
                  mx: 'auto',
                  mt: 1.5,
                  mb: 2,
                }}
              />
              
              {selectedPatient && (
                <>
                  {/* Patient Info Header */}
                  <Box sx={{ px: 3, pb: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: alpha(theme.palette.primary.main, 0.15),
                          color: theme.palette.primary.main,
                          fontWeight: 700,
                          fontSize: '1.25rem',
                        }}
                      >
                        {selectedPatient.givenName?.[0]}{selectedPatient.familyName?.[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          {selectedPatient.givenName} {selectedPatient.familyName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {selectedPatient.patientId.substring(0, 16)}...
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Divider />

                  {/* Actions List */}
                  <List sx={{ px: 1 }}>
                    <ListItemButton
                      onClick={() => handleActionAndClose(() => goToNewInteraction(selectedPatient.patientId, "initial"))}
                      data-tour="patient-actions"
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.success.main, 0.08),
                        }
                      }}
                    >
                      <ListItemIcon>
                        <AddCircleOutlineIcon sx={{ color: theme.palette.success.main }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Add New Case"
                        secondary="Start AI-assisted case creation"
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItemButton>

                    <ListItemButton
                      onClick={() => handleActionAndClose(() => openEditDialog(selectedPatient))}
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                        }
                      }}
                    >
                      <ListItemIcon>
                        <EditIcon sx={{ color: theme.palette.primary.main }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Edit Patient"
                        secondary="Update patient information"
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItemButton>

                    <ListItemButton
                      onClick={() => handleActionAndClose(() => handleCopyId(selectedPatient.patientId, 'patient'))}
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.info.main, 0.08),
                        }
                      }}
                    >
                      <ListItemIcon>
                        <ContentCopyIcon sx={{ color: theme.palette.info.main }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Copy Patient ID"
                        secondary="Copy full ID to clipboard"
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItemButton>

                    <Divider sx={{ my: 1 }} />

                    <ListItemButton
                      onClick={() => handleActionAndClose(() => openDeleteDialog(selectedPatient))}
                      sx={{
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.error.main, 0.08),
                        }
                      }}
                    >
                      <ListItemIcon>
                        <DeleteIcon sx={{ color: theme.palette.error.main }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Delete Patient"
                        secondary="Permanently remove patient record"
                        primaryTypographyProps={{ fontWeight: 600, color: 'error.main' }}
                      />
                    </ListItemButton>
                  </List>
                </>
              )}
            </Box>
          </Drawer>

          {/* Case Actions Bottom Sheet (Mobile) */}
          <Drawer
            anchor="bottom"
            open={caseActionSheetOpen}
            onClose={closeCaseActionSheet}
            PaperProps={{
              sx: {
                borderTopLeftRadius: 24,
                borderTopRightRadius: 24,
                maxHeight: '50vh',
                pb: 3,
              }
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
              {/* Drag Handle */}
              <Box
                sx={{
                  height: 4,
                  width: 40,
                  bgcolor: 'grey.300',
                  borderRadius: 2,
                  mx: 'auto',
                  mt: 1.5,
                  mb: 2,
                }}
              />
              
              {selectedCase && (
                <>
                  {/* Case Info Header */}
                  <Box sx={{ px: 3, pb: 2 }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom sx={{ fontFamily: 'monospace' }}>
                      {selectedCase.caseId?.substring(0, 24)}...
                    </Typography>
                    {selectedCase.fileName && (
                      <Typography variant="body2" color="text.secondary">
                        {selectedCase.fileName}
                      </Typography>
                    )}
                  </Box>

                  <Divider />

                  {/* Actions List */}
                  <List sx={{ px: 1 }}>
                    <ListItemButton
                      onClick={() => {
                        openCaseEditDialog(selectedCase);
                        closeCaseActionSheet();
                      }}
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                        }
                      }}
                    >
                      <ListItemIcon>
                        <AssignmentIcon sx={{ color: theme.palette.primary.main }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Assign to Patient"
                        secondary="Link this case to a patient"
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItemButton>

                    <ListItemButton
                      onClick={() => {
                        if (selectedCase.caseId) {
                          navigate(`/cases/${selectedCase.caseId}/details`);
                        }
                        closeCaseActionSheet();
                      }}
                      sx={{
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.info.main, 0.08),
                        }
                      }}
                    >
                      <ListItemIcon>
                        <VisibilityIcon sx={{ color: theme.palette.info.main }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="View Case"
                        secondary="Open case details"
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItemButton>
                  </List>
                </>
              )}
            </Box>
          </Drawer>
        </Box>
      )}
    // </>
  // );
// }
